import React, { useState, useEffect, createContext, useContext } from 'react';

const AgeVerificationContext = createContext();

export const useAgeVerification = () => {
  const context = useContext(AgeVerificationContext);
  if (!context) {
    throw new Error('useAgeVerification must be used within AgeVerificationProvider');
  }
  return context;
};

// Categories that require age verification
const ALCOHOLIC_CATEGORIES = ['Spirits', 'Wine', 'Beer', 'Soju', 'RTD'];

export const AgeVerificationProvider = ({ children }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingCallback, setPendingCallback] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null); // 'granted' | 'denied' | null

  useEffect(() => {
    // Check localStorage for existing verification
    const stored = localStorage.getItem('arkora_age_verified');
    if (stored) {
      try {
        const { verified, timestamp } = JSON.parse(stored);
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;
        
        if (verified && (now - timestamp) < twentyFourHours) {
          setIsVerified(true);
        } else {
          // Expired, clear it
          localStorage.removeItem('arkora_age_verified');
        }
      } catch (e) {
        localStorage.removeItem('arkora_age_verified');
      }
    }
  }, []);

  const requiresVerification = (category) => {
    return ALCOHOLIC_CATEGORIES.includes(category);
  };

  const verifyAge = (yearOfBirth) => {
    const currentYear = new Date().getFullYear();
    const age = currentYear - parseInt(yearOfBirth);
    
    if (age >= 18) {
      // Store verification with timestamp
      localStorage.setItem('arkora_age_verified', JSON.stringify({
        verified: true,
        timestamp: Date.now()
      }));
      setIsVerified(true);
      setVerificationResult('granted');
      
      // Execute pending callback if any
      if (pendingCallback) {
        pendingCallback();
        setPendingCallback(null);
      }
      
      // Close modal after brief delay
      setTimeout(() => {
        setShowModal(false);
        setVerificationResult(null);
      }, 1500);
      
      return true;
    } else {
      setVerificationResult('denied');
      return false;
    }
  };

  const checkAndProceed = (category, callback) => {
    if (!requiresVerification(category)) {
      // Non-alcoholic, proceed immediately
      callback();
      return;
    }

    if (isVerified) {
      // Already verified, proceed
      callback();
      return;
    }

    // Need verification
    setPendingCallback(() => callback);
    setVerificationResult(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setPendingCallback(null);
    setVerificationResult(null);
  };

  return (
    <AgeVerificationContext.Provider value={{
      isVerified,
      requiresVerification,
      checkAndProceed,
      verifyAge,
      showModal,
      closeModal,
      verificationResult
    }}>
      {children}
    </AgeVerificationContext.Provider>
  );
};

// Age Verification Modal Component
export const AgeVerificationModal = () => {
  const { showModal, closeModal, verifyAge, verificationResult } = useAgeVerification();
  const [year, setYear] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    const yearNum = parseInt(year);
    const currentYear = new Date().getFullYear();

    if (!year || isNaN(yearNum)) {
      setError('Please enter a valid year');
      return;
    }

    if (yearNum < 1900 || yearNum > currentYear) {
      setError('Please enter a valid birth year');
      return;
    }

    verifyAge(yearNum);
  };

  const handleClose = () => {
    setYear('');
    setError('');
    closeModal();
  };

  if (!showModal) return null;

  return (
    <div className="age-modal-overlay" data-testid="age-verification-modal">
      <div className="age-modal">
        {verificationResult === 'granted' ? (
          <div className="age-modal-result granted">
            <div className="age-result-icon">✓</div>
            <h2>Access Granted</h2>
            <p>Welcome to ARKORA</p>
          </div>
        ) : verificationResult === 'denied' ? (
          <div className="age-modal-result denied">
            <div className="age-result-icon">✕</div>
            <h2>Access Denied</h2>
            <p>You must be 18 years or older to view alcoholic beverage content.</p>
            <button className="btn btn-secondary" onClick={handleClose}>
              Go Back
            </button>
          </div>
        ) : (
          <>
            <div className="age-modal-header">
              <h2>Age Verification Required</h2>
              <p>This content contains alcoholic beverages.</p>
            </div>
            
            <form onSubmit={handleSubmit} className="age-modal-form">
              <div className="age-modal-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                  <path d="M11 7h2v6h-2zm0 8h2v2h-2z"/>
                </svg>
              </div>
              
              <label className="age-label">
                Enter your year of birth
              </label>
              
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                placeholder="YYYY"
                className="age-input"
                min="1900"
                max={new Date().getFullYear()}
                data-testid="age-year-input"
                autoFocus
              />
              
              {error && <p className="age-error">{error}</p>}
              
              <div className="age-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="age-verify-btn">
                  Verify Age
                </button>
              </div>
              
              <p className="age-disclaimer">
                By entering, you confirm you are of legal drinking age in your country of residence.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AgeVerificationContext;
